import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { toast } from "sonner";
import { Truck, RotateCcw, ShieldCheck } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const { add } = useCart();
  const { content } = useSiteContent();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setActive(0);
    });
  }, [id]);

  if (!product) {
    return (
      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 text-center text-[#5c5a56]">
        Loading…
      </main>
    );
  }

  const images = product.images?.length ? product.images : [];
  const c = content || {};
  const perks = [];
  if (c.shipping_badge_enabled !== false) {
    perks.push({ Icon: Truck, label: c.shipping_label || "Free shipping", sub: c.shipping_sublabel || "Pan-India" });
  }
  if (c.returns_badge_enabled !== false) {
    perks.push({ Icon: RotateCcw, label: c.returns_label || "15-day returns", sub: c.returns_sublabel || "No questions" });
  }
  if (product.warranty) {
    perks.push({ Icon: ShieldCheck, label: "Warranty", sub: product.warranty });
  }

  return (
    <main className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10 pb-24">
      <div className="grid lg:grid-cols-2 gap-12 xl:gap-20">
        {/* Gallery */}
        <div className="rise rise-1">
          <div className="relative aspect-square bg-[#f3efea] overflow-hidden">
            {images[active] ? (
              <img
                src={fileUrl(images[active])}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#c58b76] font-serif text-6xl">
                ✦
              </div>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-4 overflow-x-auto">
              {images.map((p, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`shrink-0 w-20 h-20 bg-[#f3efea] overflow-hidden border ${
                    i === active ? "border-[#c58b76]" : "border-transparent"
                  }`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="rise rise-2">
          <div className="overline">{product.category}</div>
          <h1 className="font-serif text-4xl sm:text-5xl leading-[1.05] mt-3">{product.name}</h1>
          <div className="text-xs tracking-[0.16em] uppercase text-[#5c5a56] mt-3">
            {product.material}
          </div>
          <div className="font-serif text-3xl mt-8 flex items-baseline gap-3 flex-wrap">
            <span>₹{product.price?.toLocaleString("en-IN")}</span>
            {product.compare_at_price && product.compare_at_price > product.price && (
              <>
                <span className="text-xl text-[#5c5a56] line-through" data-testid="product-mrp">
                  ₹{product.compare_at_price.toLocaleString("en-IN")}
                </span>
                <span className="text-sm uppercase tracking-[0.18em] font-medium text-[#8b3a36] bg-[#fdfbf7] border border-[#8b3a36] px-2 py-0.5" data-testid="product-savings">
                  Save ₹{(product.compare_at_price - product.price).toLocaleString("en-IN")}
                </span>
              </>
            )}
          </div>
          <div className="text-xs text-[#5c5a56] mt-1">Inclusive of all taxes · Free shipping</div>

          <p className="text-[#5c5a56] leading-relaxed mt-8">{product.description}</p>

          {product.warranty && (
            <div className="mt-6 text-sm text-[#1a1918]" data-testid="product-warranty">
              {product.warranty}
            </div>
          )}

          <div className="mt-10 flex flex-wrap gap-3">
            <button
              onClick={() => {
                add(product);
                toast.success("Added to your bag");
              }}
              className="btn-primary"
              data-testid="add-to-cart-button"
            >
              Add to bag
            </button>
            <button
              onClick={() => {
                add(product);
                navigate("/cart");
              }}
              className="btn-ghost"
              data-testid="buy-now-button"
            >
              Buy it now
            </button>
          </div>

          {perks.length > 0 && (
            <div
              className="mt-12 grid gap-6 border-t border-[#ebe5df] pt-8"
              style={{ gridTemplateColumns: `repeat(${perks.length}, minmax(0, 1fr))` }}
            >
              {perks.map(({ Icon, label, sub }, i) => (
                <div key={i} className="flex flex-col items-start gap-2">
                  <Icon size={20} className="text-[#c58b76]" strokeWidth={1.5} />
                  <div className="text-sm font-medium text-[#1a1918]">{label}</div>
                  <div className="text-[11px] tracking-[0.14em] uppercase text-[#5c5a56]">{sub}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
